import { useEffect, useMemo, useState } from 'react';
import { Plus, ClipboardList, Search, RefreshCw, Pencil, StickyNote } from 'lucide-react';
import { Input, Select } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { LogbookTimeline } from '../../components/logbook/LogbookTimeline';
import { LogbookEntryFormModal } from '../../components/logbook/LogbookEntryFormModal';
import { LogbookEntryDetailModal } from '../../components/logbook/LogbookEntryDetailModal';
import { useLogbookEntries } from '../../hooks/useLogbookEntries';
import { useLogbookTypes } from '../../hooks/useLogbookTypes';
import { useVehicles } from '../../hooks/useVehicles';
import { useDrivers } from '../../hooks/useDrivers';
import { useAuth } from '../../context/AuthContext';
import type { LogbookEntry } from '../../types/logbook';
import {
  LOGBOOK_DATE_FILTERS,
  rangoDeFiltro,
  puedeRegistrar,
  puedeCorregir,
  dentroDeVentanaEdicion,
  fueCorregida,
  esNota,
  puedeAdjuntar,
  type LogbookDateFilter,
} from '../../utils/logbook';

/**
 * FE-13 — Novedades: una sola superficie para la novedad tipificada y la
 * nota libre (ADR-015 §33 las unificó a propósito).
 *
 * El backend ya acota la visibilidad por rol en `GET /logbook-entries` —el
 * Operador ve solo las suyas, el Supervisor su sub-unidad—, así que esta
 * página no vuelve a filtrar: hacerlo sería fingir una autorización de
 * cliente. Lo que sí decide el rol es qué botones existen, y aun así el
 * servidor puede responder 403.
 */
export default function LogbookPage() {
  const { user } = useAuth();
  const [dateFilter, setDateFilter] = useState<LogbookDateFilter>('month');
  const [typeFilter, setTypeFilter] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [search, setSearch] = useState('');

  const filtro = useMemo(() => ({
    ...rangoDeFiltro(dateFilter),
    entryTypeId: typeFilter || undefined,
    vehicleId: vehicleFilter || undefined,
  }), [dateFilter, typeFilter, vehicleFilter]);

  const { entries, isLoading, error, reload, registrar, corregir } = useLogbookEntries(filtro);
  const { activos: tiposActivos, types } = useLogbookTypes();
  const { vehicles } = useVehicles();
  const { drivers } = useDrivers();

  const [formAbierto, setFormAbierto] = useState(false);
  const [detalleId, setDetalleId] = useState<string | null>(null);
  const [corrigiendoId, setCorrigiendoId] = useState<string | null>(null);

  useEffect(() => { document.title = 'Novedades | Paitron Radar'; }, []);

  // Los modales trabajan sobre la novedad VIVA de la lista, no sobre una copia
  // congelada al abrirlos: tras corregir, el objeto cambia y el detalle tiene
  // que reflejarlo sin cerrarse.
  const detalle = entries.find(e => e.id === detalleId) ?? null;
  const corrigiendo = entries.find(e => e.id === corrigiendoId) ?? null;

  const visibles = entries.filter(e => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      e.description.toLowerCase().includes(q) ||
      e.entryTypeLabel.toLowerCase().includes(q) ||
      (e.vehiclePlate ?? '').toLowerCase().includes(q) ||
      (e.driverName ?? '').toLowerCase().includes(q)
    );
  });

  const notas = entries.filter(esNota).length;
  const corregidas = entries.filter(fueCorregida).length;

  const abrirCorreccion = (entry: LogbookEntry) => {
    setDetalleId(null);
    setCorrigiendoId(entry.id);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
        {[
          { icon: <ClipboardList size={22} />, color: 'var(--accent)', valor: entries.length, label: 'Novedades en el periodo' },
          { icon: <StickyNote size={22} />, color: '#3B82F6', valor: notas, label: 'Notas libres' },
          { icon: <Pencil size={22} />, color: '#F59E0B', valor: corregidas, label: 'Corregidas' },
        ].map(s => (
          <div key={s.label} style={{ background: 'var(--bg-surface)', padding: '1.25rem', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: 'var(--radius-md)', background: `${s.color}1F`, color: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {s.icon}
            </div>
            <div>
              <span style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text-primary)', display: 'block' }}>{s.valor}</span>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{s.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 220px', minWidth: '180px' }}>
          <Input
            label="Buscar"
            placeholder="Relato, placa o conductor…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            leftIcon={<Search size={15} />}
          />
        </div>

        <div style={{ flex: '0 1 170px', minWidth: '150px' }}>
          <Select
            label="Periodo"
            value={dateFilter}
            onChange={e => setDateFilter(e.target.value as LogbookDateFilter)}
            options={LOGBOOK_DATE_FILTERS.map(f => ({ value: f.id, label: f.label }))}
          />
        </div>

        <div style={{ flex: '0 1 190px', minWidth: '150px' }}>
          <Select
            label="Tipo"
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            options={[
              { value: '', label: 'Todos los tipos' },
              // El filtro ofrece TODOS los tipos, incluidos los desactivados:
              // una novedad vieja conserva su tipo aunque hoy esté retirado, y
              // esconderlo la haría imposible de encontrar.
              ...types.map(t => ({ value: t.id, label: t.active ? t.label : `${t.label} (inactivo)` })),
            ]}
          />
        </div>

        <div style={{ flex: '0 1 190px', minWidth: '150px' }}>
          <Select
            label="Vehículo"
            value={vehicleFilter}
            onChange={e => setVehicleFilter(e.target.value)}
            options={[
              { value: '', label: 'Todos los vehículos' },
              ...vehicles.map(v => ({ value: v.id, label: v.plate })),
            ]}
          />
        </div>

        <Button variant="ghost" leftIcon={<RefreshCw size={15} />} onClick={reload} disabled={isLoading}>
          Actualizar
        </Button>

        {puedeRegistrar(user?.role) && (
          <Button leftIcon={<Plus size={16} />} onClick={() => setFormAbierto(true)}>
            Registrar novedad
          </Button>
        )}
      </div>

      {error && (
        <div style={{ padding: '0.875rem 1rem', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-lg)', color: '#EF4444', fontSize: '13px' }}>
          No se pudieron cargar las novedades: {error.message}
        </div>
      )}

      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: '1rem' }}>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><Spinner /></div>
        ) : (
          /* La más reciente primero: en el listado general de novedades lo que se busca es
             lo último que pasó. En el expediente de un viaje el orden es el
             contrario, y por eso lo decide quien llama. */
          <LogbookTimeline
            entries={visibles}
            onSelect={e => setDetalleId(e.id)}
            vacio={search.trim() ? 'Ninguna novedad coincide con la búsqueda.' : 'Sin novedades registradas en este periodo.'}
          />
        )}
      </div>

      <LogbookEntryDetailModal
        entry={detalle}
        onClose={() => setDetalleId(null)}
        onCorregir={abrirCorreccion}
        puedeCorregir={puedeCorregir(user?.role)}
        puedeAdjuntar={puedeAdjuntar(user?.role)}
        ventanaVencida={detalle ? !dentroDeVentanaEdicion(detalle, user?.role) : false}
      />

      <LogbookEntryFormModal
        isOpen={formAbierto || !!corrigiendo}
        onClose={() => { setFormAbierto(false); setCorrigiendoId(null); }}
        entry={corrigiendo}
        tipos={tiposActivos}
        vehicles={vehicles}
        drivers={drivers}
        onRegistrar={registrar}
        onCorregir={corregir}
      />
    </div>
  );
}
